import type { IBillingAdminRepository } from '@yawi/domain';
import type {
    BillingPlan,
    BillingSummary,
    CreateBillingPlanDto,
    CreatePremiumModuleDto,
    CreateSubscriptionDto,
    MarkInvoicePaidDto,
    PaginatedResult,
    PremiumModule,
    SuccessResponse,
    TenantInvoice,
    TenantSubscription,
    TenantUsage,
    TenantUsageHistory,
    UpdateBillingPlanDto,
    UpdatePremiumModuleDto,
    UpdateSubscriptionDto,
} from '@yawi/types';
import { getApiClient } from '../../http/apiClient';

export class ApiBillingAdminRepository implements IBillingAdminRepository {
  private readonly basePath = 'v1/admin/billing';

  // ============ PLANS ============
  async getAllPlans(params?: { page?: number; limit?: number; search?: string }): Promise<PaginatedResult<BillingPlan>> {
    const client = getApiClient();
    const queryParts: string[] = [];
    if (params?.page) queryParts.push(`page=${params.page}`);
    if (params?.limit) queryParts.push(`limit=${params.limit}`);
    if (params?.search) queryParts.push(`search=${encodeURIComponent(params.search)}`);
    const queryString = queryParts.length ? `?${queryParts.join('&')}` : '';
    const response = await client.get<PaginatedResult<BillingPlan>>(`${this.basePath}/plans${queryString}`);
    return response;
  }

  async getPlanById(id: string): Promise<BillingPlan> {
    const client = getApiClient();
    const response = await client.get<BillingPlan>(`${this.basePath}/plans/${id}`);
    return response;
  }

  async createPlan(dto: CreateBillingPlanDto): Promise<BillingPlan> {
    const client = getApiClient();
    const response = await client.post<BillingPlan>(`${this.basePath}/plans`, dto);
    return response;
  }

  async updatePlan(id: string, dto: UpdateBillingPlanDto): Promise<BillingPlan> {
    const client = getApiClient();
    const response = await client.put<BillingPlan>(`${this.basePath}/plans/${id}`, dto);
    return response;
  }

  async deletePlan(id: string): Promise<SuccessResponse> {
    const client = getApiClient();
    const response = await client.delete<SuccessResponse>(`${this.basePath}/plans/${id}`);
    return response;
  }

  // ============ MODULES ============
  async getAllModules(params?: { page?: number; limit?: number; search?: string }): Promise<PaginatedResult<PremiumModule>> {
    const client = getApiClient();
    const queryParts: string[] = [];
    if (params?.page) queryParts.push(`page=${params.page}`);
    if (params?.limit) queryParts.push(`limit=${params.limit}`);
    if (params?.search) queryParts.push(`search=${encodeURIComponent(params.search)}`);
    const queryString = queryParts.length ? `?${queryParts.join('&')}` : '';
    const response = await client.get<PaginatedResult<PremiumModule>>(`${this.basePath}/modules${queryString}`);
    return response;
  }

  async getModuleById(id: string): Promise<PremiumModule> {
    const client = getApiClient();
    const response = await client.get<PremiumModule>(`${this.basePath}/modules/${id}`);
    return response;
  }

  async createModule(dto: CreatePremiumModuleDto): Promise<PremiumModule> {
    const client = getApiClient();
    const response = await client.post<PremiumModule>(`${this.basePath}/modules`, dto);
    return response;
  }

  async updateModule(id: string, dto: UpdatePremiumModuleDto): Promise<PremiumModule> {
    const client = getApiClient();
    const response = await client.put<PremiumModule>(`${this.basePath}/modules/${id}`, dto);
    return response;
  }

  async deleteModule(id: string): Promise<SuccessResponse> {
    const client = getApiClient();
    const response = await client.delete<SuccessResponse>(`${this.basePath}/modules/${id}`);
    return response;
  }

  // ============ SUBSCRIPTIONS ============
  async getTenantSubscription(tenantId: string): Promise<TenantSubscription | null> {
    const client = getApiClient();
    // Sin suscripción el backend responde data: null; cualquier otro fallo se propaga.
    return client.get<TenantSubscription | null>(
      `${this.basePath}/tenants/${tenantId}/subscription`
    );
  }

  async createSubscription(tenantId: string, dto: CreateSubscriptionDto): Promise<TenantSubscription> {
    const client = getApiClient();
    const response = await client.post<TenantSubscription>(
      `${this.basePath}/tenants/${tenantId}/subscription`,
      dto
    );
    return response;
  }

  async updateSubscription(tenantId: string, dto: UpdateSubscriptionDto): Promise<TenantSubscription> {
    const client = getApiClient();
    const response = await client.put<TenantSubscription>(
      `${this.basePath}/tenants/${tenantId}/subscription`,
      dto
    );
    return response;
  }

  async cancelSubscription(tenantId: string): Promise<TenantSubscription> {
    const client = getApiClient();
    const response = await client.post<TenantSubscription>(
      `${this.basePath}/tenants/${tenantId}/subscription/cancel`
    );
    return response;
  }

  // ============ USAGE ============
  async getTenantUsage(tenantId: string): Promise<TenantUsage> {
    const client = getApiClient();
    const response = await client.get<TenantUsage>(
      `${this.basePath}/tenants/${tenantId}/usage`
    );
    return response;
  }

  async getTenantUsageHistory(tenantId: string, limit?: number): Promise<TenantUsageHistory[]> {
    const client = getApiClient();
    const queryString = limit ? `?limit=${limit}` : '';
    const response = await client.get<TenantUsageHistory[]>(
      `${this.basePath}/tenants/${tenantId}/usage/history${queryString}`
    );
    return response;
  }

  // ============ INVOICES ============
  async getTenantInvoices(tenantId: string): Promise<TenantInvoice[]> {
    const client = getApiClient();
    const response = await client.get<TenantInvoice[]>(
      `${this.basePath}/tenants/${tenantId}/invoices`
    );
    return response;
  }

  async generateInvoice(tenantId: string, period?: string): Promise<TenantInvoice> {
    const client = getApiClient();
    const queryString = period ? `?period=${period}` : '';
    const response = await client.post<TenantInvoice>(
      `${this.basePath}/tenants/${tenantId}/invoices/generate${queryString}`
    );
    return response;
  }

  async markInvoicePaid(invoiceId: string, dto?: MarkInvoicePaidDto): Promise<TenantInvoice> {
    const client = getApiClient();
    const response = await client.patch<TenantInvoice>(
      `${this.basePath}/invoices/${invoiceId}/paid`,
      dto || {}
    );
    return response;
  }

  async cancelInvoice(invoiceId: string): Promise<TenantInvoice> {
    const client = getApiClient();
    const response = await client.patch<TenantInvoice>(
      `${this.basePath}/invoices/${invoiceId}/cancel`
    );
    return response;
  }

  // ============ SUMMARY ============
  async getTenantBillingSummary(tenantId: string): Promise<BillingSummary> {
    const client = getApiClient();
    const response = await client.get<BillingSummary>(
      `${this.basePath}/tenants/${tenantId}/summary`
    );
    return response;
  }
}
